import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSocialMediaAccountService } from '@actschurch/ccms-sdk';
import { socialKeys } from '@/pages/app/SocialMediaManagement/utils/queryKeys';
import type { Platform } from '@/pages/app/SocialMediaManagement/utils/socialMediaConstants';

/**
 * Connected accounts, plus the lookups every page needs.
 *
 * Accounts change rarely, so this is cached for 30 minutes -- the same convention the
 * task lookups use. `noAccounts` is what drives the difference between "nothing is
 * connected" and "connected but no data in this window", which the empty states rely on.
 */
export function useSocialMediaAccounts() {
  const accountService = useSocialMediaAccountService();

  const { data: accounts = [], isLoading, isError, refetch } = useQuery({
    queryKey: socialKeys.accounts(),
    queryFn: () => accountService.getAll(),
    staleTime: 30 * 60 * 1000,
  });

  const accountMap = useMemo(
    () => new Map(accounts.map((a) => [String(a.id), a])),
    [accounts],
  );

  const accountOptions = useMemo(
    () => accounts.map((a) => ({
      value: String(a.id),
      label: `${a.accountName ?? 'Unnamed'} (${a.platform})`,
    })),
    [accounts],
  );

  /** Platforms actually connected, so filters offer only what exists. */
  const connectedPlatforms = useMemo(
    () => Array.from(new Set(accounts.map((a) => a.platform).filter(Boolean))) as Platform[],
    [accounts],
  );

  return {
    accounts,
    accountMap,
    accountOptions,
    connectedPlatforms,
    noAccounts: !isLoading && !isError && accounts.length === 0,
    isLoading,
    isError,
    refetch,
    getAccountName: (id?: string | null) =>
      (id && accountMap.get(String(id))?.accountName) || 'Unknown account',
  };
}
